import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { authService } from '@/services/auth/authService';
import { useAuth } from '@/contexts/AuthContext';
import { authLogger } from '@/utils/logger';

/**
 * Landing route for the authorization-code callback (`/auth/callback`), shared by the
 * silent `prompt=none` round and the interactive login (D13b).
 *
 * On success the token family is in memory — we resume via a CLIENT-SIDE navigation so
 * the in-memory tokens survive (a full reload would wipe them). On `login_required`
 * (silent round, no live auth-host cookie) we fall back to the interactive login page,
 * never a redirect loop. Any other failure lands on the same page, flagged
 * `signin=failed` so it says the attempt did not complete.
 */
export function AuthCallback() {
  const navigate = useNavigate();
  const { refreshAuthState } = useAuth();
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    (async () => {
      try {
        const returnUrl = await authService.completeSignin();
        refreshAuthState();
        navigate(returnUrl, { replace: true });
      } catch (err) {
        if (authService.isInteractionRequired(err)) {
          // Silent round could not complete without user interaction. The automatic
          // attempt is spent; `interaction=required` tells the login screen to surface
          // an EXPLICIT sign-in action rather than starting another authorize round on
          // its own (D18 item 36). That matters most on a tenant subdomain reached
          // while an auth-host session for a different tenant is still live: an
          // automatic round there could complete off the live Entra session and switch
          // the user's tenant without them ever acting.
          navigate('/login?interaction=required', { replace: true });
          return;
        }
        // Any other failure — e.g. "No matching state found in storage" when the callback
        // lands in a tab that did not start the round, or a replayed callback URL. The
        // code is unusable either way, so the way forward is a new round: land on the
        // same explicit "Continue to sign-in" screen, told to say why. Never restart the
        // round automatically (no loop, and D18 item 36 holds).
        authLogger.error('Auth callback failed', err instanceof Error ? err : undefined);
        navigate('/login?interaction=required&signin=failed', { replace: true });
      }
    })();
  }, [navigate, refreshAuthState]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
      }}
    >
      <CircularProgress />
      <Typography color="text.secondary">Signing you in…</Typography>
    </Box>
  );
}
